// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StarX from "@conklin/MobiusOverrides/Icons/StarX";
import { TooltipPresentationProps } from "@conklin/MobiusOverrides/Tooltip";
import { wishListCardStyles } from "@conklin/Overrides/Components/WishListCard";
import IconListsUser from "@conklin/XDL/Icons/IconListsUser";
import IconNotificationInfo from "@conklin/XDL/Icons/IconNotificationInfo";
import { color, ellipsis, moreActionsSharedStyle, xScreen } from "@conklin/XDL/Utilities";
import { StyledButton } from "@insite/mobius/Clickable";
import Star from "@insite/mobius/Icons/Star";
import Tag from "@insite/mobius/Icons/Tag";
import TypographyStyle from "@insite/mobius/Typography/TypographyStyle";
import { css } from "styled-components";

wishListCardStyles.container = {
    css: css`
        display: grid;
        grid-template-columns: minmax(317px, 430px) minmax(0, 317px) minmax(95px, 160px) minmax(165px, 204px);
        grid-column-gap: 22px;
        justify-content: space-between;
        ${xScreen("lte", "md")} {
            grid-template-columns: minmax(317px, 1fr) minmax(95px, 160px) minmax(165px, 232px);
        }
        ${xScreen("lte", "sm")} {
            grid-template-columns: minmax(0, 1fr);
        }
        grid-row-gap: 12px;
        width: 100%;
    `,
};

// Description Section Styles
wishListCardStyles.descriptionWrapper = {
    css: css`
        grid-column: 1 / 2;
        grid-row: 1 / 2;
        ${xScreen("lte", "md")} {
            grid-column: 1 / 4;
        }
        ${xScreen("lte", "sm")} {
            grid-column: 1 / 2;
        }
        > div {
            display: flex;
            flex-direction: column;
            gap: 12px;
        }
    `,
};

wishListCardStyles.nameWrapper = {
    css: css`
        & > ${StyledButton} {
            font-weight: bold;
            ${TypographyStyle} {
                font-size: 16px;
                line-height: 19px;
            }
        }
    `,
};

wishListCardStyles.extendedInfoGridItem = {
    width: [12, 12, 7, 6, 6],
    align: "middle",
    css: css`
        align-items: center;
    `,
};

wishListCardStyles.sharingStatusGridItem = {
    width: 12,
    css: css`
        align-items: center;
        font-size: 12px;
        line-height: 13px;
        gap: 20px;
        ${xScreen("lte", "md")} {
            justify-content: flex-start;
            &:empty {
                display: none;
            }
        }
    `,
};

wishListCardStyles.actionGridMediumHidden = {
    css: css`
        width: auto;
        display: flex;
        flex-direction: column;
        gap: 16px;
    `,
};

wishListCardStyles.productImage = {
    width: "92px",
    height: "92px",
    css: css`
        ${xScreen("lte", "sm")} {
            width: 72px;
            height: 72px;
        }
        img {
            height: auto;
            max-height: 100%;
            max-width: 100%;
        }
    `,
    errorTypographyProps: {
        size: 11,
        css: css`
            max-height: 65px;
        `,
    },
};

wishListCardStyles.productImagesGridItem = {
    width: 12,
    css: css`
        justify-content: flex-start;
        align-items: center;
        flex-wrap: wrap;
        gap: 21px;

        ${xScreen("lte", "md")} {
            &:empty {
                display: none;
            }
        }

        ${xScreen("lte", "sm")} {
            padding: 13px 15px 10px;
        }
    `,
};

wishListCardStyles.lastUpdatedText = {
    size: 12,
    lineHeight: "17px",
};

wishListCardStyles.descriptionText = {
    size: 12,
    lineHeight: "17px",
    css: css`
        ${ellipsis(2)}
    `,
};

wishListCardStyles.actionFavoriteClickable = {
    css: css`
        width: auto;
        font-size: 12px;
        line-height: 17px;
        font-weight: bold;
        color: ${color("common.ui9")};
        &:hover {
            color: ${color("common.ui10")};
        }
    `,
};

wishListCardStyles.favTagsWrapper = {
    css: css`
        display: flex;
        column-gap: 24px;
        > div {
            line-height: 17px;
        }
    `,
};

wishListCardStyles.starIcon = {
    src: Star,
    size: 14,
    color: "text.link",
    css: css`
        margin-right: 6px;
        opacity: 0.5;
    `,
};

wishListCardStyles.starXIcon = {
    src: StarX,
    size: 14,
    color: "text.link",
    css: css`
        margin-right: 6px;
        opacity: 0.5;
    `,
};

wishListCardStyles.tagTooltip = {
    mergeCss: true,
    extendedStyles: {
        triggerWrapper: {
            css: css`
                display: flex;
                align-items: center;
                font-size: 12px;
                line-height: 17px;
                margin: 0;
                gap: 6px;
                font-weight: bold;
                color: ${color("common.ui9")};
                cursor: pointer;
                &:hover {
                    color: ${color("common.ui10")};
                }
            `,
        },
    },
} as TooltipPresentationProps;

wishListCardStyles.tagIcon = {
    src: Tag,
    size: 12,
    color: "text.link",
    css: css`
        opacity: 0.5;
    `,
};

// Image Section Styles
wishListCardStyles.imagesWrapper = {
    css: css`
        grid-column: 2 / 3;
        grid-row: 1 / 2;
        display: flex;
        gap: 22px;
        ${xScreen("lte", "md")} {
            grid-column: 1 / 2;
            grid-row: 2 / 3;
        }
    `,
};

wishListCardStyles.productImageLink = {
    css: css`
        display: flex;
        align-items: flex-start;
        margin: 0;
        &:has(> span:empty) {
            display: none;
        }
    `,
};

wishListCardStyles.productImage = {
    mergeCss: true,
    width: "91",
    height: "91",
    css: css`
        img {
            width: 100%;
            height: 100%;
            object-fit: contain;
        }
    `,
};

// sharing status Wrapper
wishListCardStyles.sharingStatusWrapper = {
    css: css`
        grid-column: 3 / 4;
        grid-row: 1 / 2;
        display: flex;
        align-items: flex-start;
        ${xScreen("lte", "md")} {
            grid-column: 2 / 3;
            grid-row: 2 / 3;
        }
        ${xScreen("lte", "sm")} {
            grid-column: 1 / 2;
            grid-row: 3 / 4;
        }
    `,
};

// actions Wrapper
wishListCardStyles.actionsWrapper = {
    css: css`
        grid-column: 4 / 5;
        grid-row: 1 / 2;
        ${xScreen("lte", "md")} {
            grid-column: 3 / 4;
            grid-row: 2 / 3;
        }
        ${xScreen("lte", "sm")} {
            grid-column: 1 / 2;
            grid-row: 4 / 5;
        }
    `,
};

wishListCardStyles.actionGridMediumHidden = {
    css: css`
        display: flex;
        flex-direction: column;
        gap: 12px;
        width: 100%;
    `,
};

wishListCardStyles.overflowMenu = {
    ...moreActionsSharedStyle.overflowMenu,
};

wishListCardStyles.moreActions = {
    ...moreActionsSharedStyle.moreActionsLink,
};

wishListCardStyles.sharingStatus = {
    statusText: {
        size: 12,
        lineHeight: "15px",
        css: css`
            span {
                font-size: 12px;
                line-height: 15px;
            }
        `,
    },
    privateTooltip: {
        iconProps: {
            src: IconNotificationInfo,
            size: 12,
            color: "success.main",
        },
    },
    sharedIcon: {
        src: IconListsUser,
        size: 12,
        color: "success.main",
    },
};
